import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, TextField, Typography, MenuItem } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import moment from 'moment-timezone';
import { isEmpty, min } from 'lodash';
import { useTranslation } from 'services/localization/useTranslation';
import { usePermissions } from 'hooks/usePermissions';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import * as RentalSelectors from 'selectors/RentalViewSelectors';
import { localFormat } from 'common/utils/dateUtils';
import { getPricingString } from 'common/utils/common';
import { getTranslation } from 'common/modules/translations';
import { DurationWithPrice, OpeningHoursWithExceptions, OrderProduct } from 'common/types';
import { getDurationTypeFromTimePeriod, getLabelForDurationWithPrice } from 'common/utils/pricing';
import { getSelectedSkusCount, ProductTotalAvailability } from 'common/api/frontend/inventory_new';
import { getUniqueProducts } from 'common/utils/rentalUtils';
import { notNull } from 'common/utils/common';
import { dateIsOutsideOpeningHours, getNewEndTime } from 'common/utils/dateCalculations';
import { getAvailabilityAmountsBySkuForTimePeriod } from 'common/api/frontend/inventory_new/availabilityRange';
import { getProductInventoryBlockerMinutes } from 'common/utils/productUtils';
import { AvailabilityStatusBubble } from 'components/AvailabilityStatusBubble';
import { AvailabilityStatusType } from 'services/types';
import { getVariantSkuId } from 'common/modules/products/variants';

interface Props {
	options: DurationWithPrice[];
	value: number;
	productsNotYetReturned: OrderProduct[];
	loading: boolean;
	productAvailabilities: ProductTotalAvailability[] | null;
	openingHours: OpeningHoursWithExceptions;
	showPriceChange?: boolean;
	noPrice?: boolean;
	disabled?: boolean;
	currentPrice?: number;
	onValueChange: (durationOption: DurationWithPrice | null) => void;
	useAvailabilities?: boolean;
}
const DurationDropdownWithAvailability = (props: Props) => {
	const {
		options,
		noPrice,
		showPriceChange,
		currentPrice,
		onValueChange,
		value,
		disabled,
		openingHours,
		productsNotYetReturned,
		productAvailabilities,
		loading,
		useAvailabilities = true,
	} = props;
	const classes = useStyles();
	const { t, lang } = useTranslation();
	const { hasPermission } = usePermissions();
	const allowOverbookings = hasPermission('allow_overbookings');

	const shopDateFormat = useSelector(ShopSelectors.getShopDateFormat);
	const currency = useSelector(ShopSelectors.getShopCurrency);
	const shopTimeZone = useSelector(ShopSelectors.getShopTimeZone);
	const stockProducts = useSelector(StockSelectors.stockProductsData);
	const stockProductsMap = useSelector(StockSelectors.stockProductsById);

	const order = useSelector(RentalSelectors.activeOrder)!;
	const { rentalInfo } = order;
	const selectedOption = options[value] ?? undefined;

	const skusOfProductsNotYetReturned = useMemo(() => getSelectedSkusCount(productsNotYetReturned), [
		productsNotYetReturned,
	]);

	const blockerMinutesBySkuId: { [skuId: string]: number } = useMemo(() => {
		const products = getUniqueProducts(productsNotYetReturned, stockProductsMap);
		const inventoryBlockerMinutesBySkuId = products.reduce((tot, product) => {
			const inventoryBlockerMinutes = getProductInventoryBlockerMinutes(product, stockProducts);
			product.variants.options.forEach((variant) => {
				const skuId = getVariantSkuId(variant);
				if (!skuId) return;
				const previousBlockerMinutes = tot[skuId];
				tot[skuId] =
					!previousBlockerMinutes || previousBlockerMinutes < inventoryBlockerMinutes
						? inventoryBlockerMinutes
						: previousBlockerMinutes;
			});
			return tot;
		}, {} as { [skuId: string]: number });
		return inventoryBlockerMinutesBySkuId;
	}, [productsNotYetReturned, stockProductsMap, stockProducts]);

	const handleDurationChange = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const index = Number(event.target.value);
		const selectedOption = options[index] || null;
		onValueChange(selectedOption);
	};

	const checkAvailabilityForDuration = (
		availabilitiesPerSkuForPeriod:
			| {
					[skuId: string]: number | null;
			  }
			| undefined,
		endDate: moment.Moment,
	) => {
		let isBlocked: boolean = false;
		let notEnoughAvailability: boolean = false;
		let isOutsideOpeningHours: boolean = false;

		if (!useAvailabilities) {
			isBlocked = false;
		}
		//if loading availability data or no product availabilities
		else if (!availabilitiesPerSkuForPeriod || isEmpty(availabilitiesPerSkuForPeriod)) {
			isBlocked = true;
		} else {
			const availabilitiesWithNotReturnedSkus: number[] = Object.entries(
				availabilitiesPerSkuForPeriod,
			)
				.filter(([skuId]) => !!skusOfProductsNotYetReturned[skuId])
				.map(([skuId, count]) => {
					const notReturnedAmount = skusOfProductsNotYetReturned[skuId];
					return count !== null ? count - notReturnedAmount : null;
				})
				.filter(notNull);

			const minAvailabilityForPeriod = min(availabilitiesWithNotReturnedSkus);
			/*
			 * if minAvailabilityForPeriod === undefined =>
			 * availabilitiesWithNotReturnedVariants is empty =>
			 * all values have been null => unlimited availability
			 */
			if (!!minAvailabilityForPeriod || minAvailabilityForPeriod === 0) {
				notEnoughAvailability = minAvailabilityForPeriod < 0;
			}
			isOutsideOpeningHours = dateIsOutsideOpeningHours(endDate, openingHours);

			isBlocked = notEnoughAvailability || isOutsideOpeningHours;
		}
		return { isBlocked, notEnoughAvailability, isOutsideOpeningHours };
	};

	const renderDropdownItem = (option: DurationWithPrice, index: number) => {
		const durationType = getDurationTypeFromTimePeriod(option.timePeriod);
		const durationLabel = getLabelForDurationWithPrice(option, lang, t);
		const priceChange = currentPrice ? option.price - currentPrice : 0;

		const optionForEndDate = {
			...option,
			durationType,
			durationName: option.label || null,
		};

		const endDate = moment(
			getNewEndTime(rentalInfo.startDate, optionForEndDate, openingHours, shopTimeZone),
		);

		const bookingStartDate = moment(rentalInfo.startDate);
		const availabilityStartDate =
			bookingStartDate.isAfter(moment()) || endDate.isBefore(moment())
				? bookingStartDate
				: moment();

		const availabilitiesPerSkuVariantForPeriod = productAvailabilities
			? getAvailabilityAmountsBySkuForTimePeriod(
					availabilityStartDate,
					endDate,
					productAvailabilities,
					blockerMinutesBySkuId,
			  )
			: undefined;

		const {
			isBlocked,
			notEnoughAvailability,
			isOutsideOpeningHours,
		} = checkAvailabilityForDuration(availabilitiesPerSkuVariantForPeriod, endDate);

		const isSelectedTimeSlot = index === value;
		const disabledItem = loading || (!allowOverbookings && isBlocked);

		const getSecondaryText = () => {
			return (
				<Typography
					variant="caption"
					color="textSecondary"
					component="div"
					className={classes.secondaryText}
				>
					<Box lineHeight="normal">
						{notEnoughAvailability
							? t('individualRental.notEnoughProductsAvailable', 'Not enough products available')
							: isOutsideOpeningHours
							? t(
									'individualRental.endsOutsideOpeningHours2',
									'Order ends outside of opening hours.',
							  )
							: durationType === 'opening_hours'
							? option.endTime
								? t('newRental.shopClosing') +
								  ' ' +
								  `(${localFormat(option.endTime, 'ddd HH:mm', shopDateFormat)})`
								: t('newRental.shopClosing')
							: ''}
					</Box>
				</Typography>
			);
		};

		const availabilityStatus: AvailabilityStatusType = loading
			? 'loading'
			: !isBlocked
			? 'available'
			: 'unavailable';

		return (
			<MenuItem
				className={isSelectedTimeSlot ? classes.selectedTimeSlot : undefined}
				disabled={disabledItem}
				key={`${option.durationInSeconds}_${index}`}
				value={index}
			>
				<div className={classes.optionWrapper}>
					{useAvailabilities && (
						<div className={classes.statusAvatar}>
							<AvailabilityStatusBubble status={availabilityStatus} />
						</div>
					)}
					<div className={classes.primaryOption}>
						<div className={classes.labelContainer}>
							<Typography component="div" className={classes.label}>
								<Box lineHeight="normal">{durationLabel}</Box>
							</Typography>
							{!noPrice && !showPriceChange ? (
								<Typography className={classes.secondaryOption} component="div">
									<Box lineHeight="normal">
										<b>{getPricingString(option.price, currency)}</b>
									</Box>
								</Typography>
							) : showPriceChange && priceChange ? (
								<Typography className={classes.secondaryOption} component="div">
									<Box lineHeight="normal">
										<b className={classes.price}>
											{(priceChange > 0 ? '+ ' : '- ') +
												getPricingString(Math.abs(priceChange), currency)}
										</b>
									</Box>
								</Typography>
							) : null}
						</div>
						{(isBlocked || durationType === 'opening_hours') && getSecondaryText()}
					</div>
				</div>
			</MenuItem>
		);
	};

	return (
		<TextField
			id="duration"
			name="duration"
			label={t('shopperCard.durationAndPricing', 'Duration and pricing')}
			select
			variant={'outlined'}
			className={classes.textField}
			margin={'none'}
			type="text"
			value={value !== -1 ? value : ''} // Empty here is to indicate empty value of dropdown, required by MaterialUI
			SelectProps={{
				renderValue: (value) =>
					value !== '__EMPTY__' ? (
						<Typography component="div" className={classes.label}>
							<Box lineHeight="normal">{getLabelForDurationWithPrice(selectedOption, lang, t)}</Box>
						</Typography>
					) : (
						<Typography component="div" className={classes.label}>
							<Box lineHeight="normal">
								{productsNotYetReturned[0].durationName
									? getTranslation(productsNotYetReturned[0].durationName, lang)
									: ''}
							</Box>
						</Typography>
					),
			}}
			onChange={handleDurationChange}
			disabled={disabled}
		>
			{options.map((option, index) => {
				return renderDropdownItem(option, index);
			})}
		</TextField>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		textField: {
			width: '100%',
		},
		optionWrapper: {
			width: '100%',
			display: 'flex',
			justifyContent: 'space-between',
			lineHeight: 1,
			alignItems: 'center',
		},
		primaryOption: {
			flex: 1,
			margin: theme.spacing(1, 0),
		},
		labelContainer: {
			display: 'flex',
		},
		secondaryOption: {
			textAlign: 'right',
			fontWeight: 400,
			alignItems: 'baseline',
		},
		statusAvatar: {
			minWidth: 0,
		},
		selectedTimeSlot: {
			background: theme.palette.action.selected,
		},
		secondaryText: {
			marginTop: theme.spacing(1),
		},
		label: {
			margin: 0,
			flex: 1,
		},
		price: {
			fontSize: 14,
		},
	}),
);

export default DurationDropdownWithAvailability;
